/**
 * PlayerManager - Local and remote player lifecycle management
 * 
 * Extracted from GameCore to handle:
 * - Local player creation and control
 * - Remote player creation/destruction from snapshots
 * - Input gathering and sending
 * - Server state reconciliation
 */

import * as THREE from 'three';
import { PlayerLocal, RespawnCallback } from './player/playerLocal';
import { PlayerRemote, getPlayerColor, playerColorToHex } from './player/playerRemote';
import { Controls, controls } from './player/controls';
import { sendBinary } from '../net/netClient';
import {
  CLIENT_INPUT_HZ,
  RoomSnapshot,
  encodeInput,
  type VoxelField,
  type VoxelPhysicsWorld,
} from '@worldify/shared';
import { useGameStore } from '../state/store';

/** Callback type for finding respawn positions (decouples from VoxelIntegration) */
export type RespawnFinder = (currentPos: THREE.Vector3, lastGroundedPos: THREE.Vector3 | null) => THREE.Vector3 | null;

export class PlayerManager {
  private localPlayer: PlayerLocal;
  private remotePlayers = new Map<number, PlayerRemote>();
  private localPlayerId: number | null = null;
  
  // Respawn position finder (injected by GameCore)
  private respawnFinder: RespawnFinder | null = null;

  // Input sending interval
  private inputInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.localPlayer = new PlayerLocal();
  }

  /**
   * Get the local player instance
   */
  getLocalPlayer(): PlayerLocal {
    return this.localPlayer;
  }

  /**
   * Set the local player ID (assigned by server)
   */
  setLocalPlayerId(playerId: number): void {
    this.localPlayerId = playerId;
  }

  /**
   * Get the local player ID
   */
  getLocalPlayerId(): number | null {
    return this.localPlayerId;
  }

  /**
   * Get the local player's skin color as a CSS hex string
   */
  getLocalPlayerColor(): string {
    return playerColorToHex(getPlayerColor(this.localPlayerId ?? 0));
  }

  /**
   * Point the local player's physics at the voxel world and wire up respawn.
   * Returns the VoxelField so the caller can invalidate chunks on ingest/unload.
   */
  setPhysicsWorld(source: VoxelPhysicsWorld): VoxelField {
    const field = this.localPlayer.setWorldSource(source);
    this.localPlayer.setRespawnCallback(this.handleRespawnNeeded);
    return field;
  }

  /**
   * Set the respawn finder callback (injected by GameCore).
   * Decouples PlayerManager from VoxelIntegration for respawn logic.
   */
  setRespawnFinder(finder: RespawnFinder): void {
    this.respawnFinder = finder;
  }

  /**
   * Handle respawn request from local player (fell too long)
   */
  private handleRespawnNeeded: RespawnCallback = (currentPos, lastGroundedPos) => {
    // WHEN WE CANNOT PLACE THE PLAYER, DO NOT GUESS — LEAVE THEM WHERE THEY ARE.
    //
    // Both branches here used to teleport to (0, SPAWN_FALLBACK_HEIGHT, 0). That constant is 50 m,
    // which was above the old ~55 m world; on the landform terrain it is INSIDE THE GROUND for
    // 37.2% of columns (measured: the surface spans -141 m to 915 m). Teleporting a player into
    // rock is strictly worse than not moving them: the depenetration ladder's escape scan only
    // reaches 16 m, so a player dropped inside a mountain is wedged, and on an initial spawn there
    // is no last-valid pose to restore them to either.
    //
    // Not moving is safe by construction. The only way to get here is that no ground was found,
    // which means the terrain is not resident — and the unknown-space freeze already holds a player
    // still in exactly that situation. The fall check re-arms and asks again once chunks arrive.
    if (!this.respawnFinder) {
      console.warn('[PlayerManager] No respawn finder set — holding position until one is');
      return;
    }

    const respawnPos = this.respawnFinder(currentPos, lastGroundedPos);
    if (respawnPos) {
      this.localPlayer.respawn(respawnPos);
    }
  };

  /**
   * Place the local player at a spawn position (marker spawn, first spawn, saved-pose restore).
   *
   * MUST go through the physics teleport, not `position.copy()`. `PlayerLocal.position` is a
   * RENDER MIRROR that every tick re-writes from the authoritative sim, so copying into it moved
   * the player for exactly one frame: the first tick snapped them back to the sim's pose — the
   * origin on a fresh session, which is inside rock on landform terrain, so the capsule wedged,
   * reported `stuck`, and the respawn path put it on the origin surface. That was the whole of
   * "Play always teleports me back to the original spawn". Pinned by playerLocal.spawn.test.ts.
   */
  setSpawnPosition(position: THREE.Vector3): void {
    this.localPlayer.spawnAt(position);
  }

  /**
   * Start the input sending loop
   */
  startInputLoop(): void {
    if (this.inputInterval !== null) return;

    const intervalMs = 1000 / CLIENT_INPUT_HZ;
    this.inputInterval = setInterval(() => {
      this.sendInput();
    }, intervalMs);
  }

  /**
   * Stop the input sending loop
   */
  stopInputLoop(): void {
    if (this.inputInterval !== null) {
      clearInterval(this.inputInterval);
      this.inputInterval = null;
    }
  }

  /**
   * Send current input to server
   */
  private sendInput(): void {
    const input = this.localPlayer.getInput(controls);
    sendBinary(encodeInput(input));
  }

  /**
   * Update local player physics
   */
  updateLocalPlayer(deltaMs: number): void {
    this.localPlayer.update(deltaMs, controls);
  }

  /**
   * Update all remote players (interpolation)
   */
  updateRemotePlayers(deltaMs: number): void {
    for (const remote of this.remotePlayers.values()) {
      remote.update(deltaMs);
    }
  }

  /**
   * Get the controls instance
   */
  getControls(): Controls {
    return controls;
  }

  /**
   * Handle incoming snapshot from server
   * Updates local player server state and manages remote player lifecycle
   */
  handleSnapshot(snapshot: RoomSnapshot, scene: THREE.Scene): void {
    // Update player count in store
    useGameStore.getState().setPlayerCount(snapshot.players.length);

    // Track which players we've seen
    const seenPlayerIds = new Set<number>();

    for (const playerData of snapshot.players) {
      seenPlayerIds.add(playerData.playerId);

      // Skip local player (we control them locally)
      if (playerData.playerId === this.localPlayerId) {
        // Update local player position from server (for now, no prediction)
        this.localPlayer.applyServerState(playerData);
        continue;
      }

      // Get or create remote player
      let remote = this.remotePlayers.get(playerData.playerId);
      if (!remote) {
        remote = new PlayerRemote(playerData.playerId);
        this.remotePlayers.set(playerData.playerId, remote);
        scene.add(remote.mesh);
      }

      remote.applySnapshot(playerData);
    }

    // Remove disconnected players
    for (const [playerId, remote] of this.remotePlayers) {
      if (!seenPlayerIds.has(playerId)) {
        scene.remove(remote.mesh);
        remote.dispose();
        this.remotePlayers.delete(playerId);
      }
    }
  }

  /**
   * Get positions of all remote players for map overlay.
   */
  getRemotePlayerPositions(): Array<{ x: number; z: number; rotation: number; color: string }> {
    const positions: Array<{ x: number; z: number; rotation: number; color: string }> = [];
    for (const remote of this.remotePlayers.values()) {
      positions.push({
        x: remote.mesh.position.x,
        z: remote.mesh.position.z,
        rotation: remote.yaw,
        color: playerColorToHex(getPlayerColor(remote.playerId)),
      });
    }
    return positions;
  }

  /**
   * Dispose all players and clean up
   */
  dispose(): void {
    this.stopInputLoop();

    // Clean up remote players
    for (const remote of this.remotePlayers.values()) {
      remote.dispose();
    }
    this.remotePlayers.clear();
  }
}
